"use client";

import { Card, CardContent, Stack, Typography, useTheme } from "@mui/material";
import { SLLink } from "@/components/i18n/SLLink";
import { ReactNode } from "react";

interface SLPreviewCardProps {
  href: string;
  title: string | ReactNode;
  summary: string | ReactNode;
  authors?: Array<{
    id: string;
    name: string;
    image: string;
  }>;
  date?: string;
  children?: ReactNode;
}

export const SLPreviewCard = ({
  href,
  title,
  summary,
  authors,
  date,
  children,
}: SLPreviewCardProps) => {
  const theme = useTheme();

  return (
    <SLLink href={href} style={{ textDecoration: "none", height: "100%" }}>
      <Card
        component="article"
        sx={{
          display: "flex",
          position: "relative",
          height: "100%",
          flexDirection: "column",
          textDecoration: "none",
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",
          transition: "transform 0.3s ease-out, background-color 0.2s ease",
          "&:hover": {
            backgroundColor: theme.palette.warning.main,
            transform: "translateY(-8px)",
          },
          borderRadius: "0",
        }}
      >
        <CardContent
          sx={{
            flexGrow: 1,
            height: "stretch",
          }}
        >
          <Stack
            sx={{
              justifyContent: "space-between",
              height: "stretch",
              gap: 3,
              py: 1,
            }}
          >
            {date && (
              <Stack width="100%" alignItems="flex-end">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1" color="text.primary">
                    {date}
                  </Typography>
                </Stack>
              </Stack>
            )}

            <Typography
              variant="h5"
              component="h3"
              gutterBottom
              sx={{
                color: theme.palette.text.primary,
                lineHeight: 1.3,
                mb: 5,
              }}
            >
              {title}
            </Typography>

            <Stack spacing={1} gap={3} sx={{ flexDirection: "column" }}>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 3,
                  flexGrow: 1,
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.5,
                }}
              >
                {summary}
              </Typography>

              {authors && authors.length > 0 && (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      {authors.map((author, index) => (
                        <Stack
                          key={author.id}
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.875rem"
                          >
                            {author.name}
                            {index < authors.length - 1 && ", "}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              )}

              {children}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </SLLink>
  );
};
